import type { RouteRecordRaw } from 'vue-router'
import { TRANSPORT_CONSIGNMENT_PERM } from '@/views/order/transport-consignment/transport-consignment-privilege'
import { canEnterReportCenter } from '@/views/report-center/transport-consignment/utils/report-route-permission'

/**
 * 租户端路由配置（需要权限控制）
 */
export const adminRoutes: RouteRecordRaw[] = [
  // 主布局路由
  {
    path: '/',
    name: 'Layout',
    component: () => import('@/layouts/MainLayout.vue'),
    children: [
      // 首页 - 不需要权限验证，排序第一
      {
        path: '/home',
        name: 'Home',
        component: () => import('@/views/home/index.vue'),
        meta: {
          title: '首页',
          icon: 'icon-home',
          requiresAuth: false,
          order: 1
        }
      },
      // BI统计分析
      {
        path: '/bi-analytics',
        name: 'BiAnalytics',
        component: () => import('@/views/bi-analytics/index.vue'),
        meta: {
          title: 'BI统计分析',
          icon: 'icon-dashboard',
          requiresAuth: false,
          order: 1,
          permission: 'module:bi-analytics:bi-analytics'
        }
      },
      // 仪表盘
      {
        path: '/dashboard',
        name: 'Dashboard',
        component: () => import('@/views/dashboard/index.vue'),
        meta: {
          title: '仪表盘',
          icon: 'icon-dashboard',
          requiresAuth: false,
          order: 1,
          permission: 'module:dashboard:dashboard'
        }
      },
      // 委托下单（CCTOWER-2170，与原型一致：仪表盘下方独立菜单）
      {
        path: '/order/unified-create',
        name: 'OrderUnifiedCreate',
        component: () => import('@/views/order/OrderCreatePage.vue'),
        meta: {
          title: '委托下单',
          icon: 'icon-file',
          requiresAuth: false,
          order: 98,
          permission: 'module:order-management:order-management',
          requiresUnifiedOrderV2: true,
          preserveRouteMeta: true,
          insertAfterPermission: 'module:dashboard:dashboard',
          hideForClient: true
        }
      },

      // 开发环境专用路由
      ...(process.env.NODE_ENV === 'development' ? [
        {
          path: '/components-demo',
          name: 'ComponentsDemo',
          meta: {
            title: '组件示例',
            icon: 'icon-code',
            requiresAuth: false,
            order: 2.5,
            badge: 'Demo',
            isMenuGroup: true
          },
          children: [
            {
              path: 'example-page',
              name: 'ExamplePage',
              component: () => import('@/views/ExamplePage.vue'),
              meta: {
                title: '示例页面',
                requiresAuth: false,
                order: 1
              }
            },
            {
              path: 'advanced-select',
              name: 'AdvancedSelectExample',
              component: () => import('@/views/AdvancedSelectExample.vue'),
              meta: {
                title: '高级选择组件',
                requiresAuth: false,
                order: 2
              }
            },
            {
              path: 'ui-config-management',
              name: 'UiConfigManagement',
              component: () => import('@/views/system-management/ui-config-management.vue'),
              meta: {
                title: '筛选方案管理',
                requiresAuth: false,
                order: 3,
                badge: 'DEV'
              }
            },
            {
              path: 'permission-usage',
              name: 'PermissionUsageExample',
              component: () => import('@/views/PermissionUsageExample.vue'),
              meta: {
                title: '权限使用说明',
                requiresAuth: false,
                order: 4,
                badge: 'DOC'
              }
            }
          ]
        }
      ] : []),

      // 控制塔面板 - 核心功能
      {
        path: '/control-tower-panel',
        name: 'ControlTowerPanel',
        component: () => import('@/views/control-tower-panel/index.vue'),
        meta: {
          title: '控制塔面板',
          icon: 'icon-thunderbolt',
          requiresAuth: false,
          order: 2,
          permission: 'module:control-tower:control-tower'
        }
      },

      // 超级运价系统 - 使用简单的路由层级结构
      {
        path: '/saas',
        name: 'SuperRates',
        meta: {
          title: '超级运价',
          icon: 'icon-file',
          requiresAuth: false,
          order: 3,
          permission: 'module:super-freight:super-freight'
        },
        children: [
          {
            path: 'rate-management',
            name: 'SaasRates',
            component: () => import('@/views/saas/rate-management/index.vue'),
            meta: {
              title: '运价维护',
              requiresAuth: false,
              order: 1,
              permission: 'module:rate-maintenance:rate-maintenance'
            }
          },
          // FCL整箱运价列表页面
          {
            path: 'rate-management/fcl',
            name: 'SaasFclRates',
            component: () => import('@/views/saas/rate-management/fcl/index.vue'),
            meta: {
              title: 'FCL整箱运价',
              requiresAuth: false,
              hidden: true // 不显示在菜单中
            }
          },
          // FCL文字运价AI解析预览页面 (CCTOWER-3605)
          {
            path: 'rate-management/fcl/text-import-preview',
            name: 'SaasFclTextRatePreview',
            component: () => import('@/views/saas/rate-management/fcl/text-rate-preview.vue'),
            meta: {
              title: '文字运价解析预览',
              requiresAuth: false,
              hidden: true // 不显示在菜单中
            }
          },
          // 港前运价列表页面
          {
            path: 'rate-management/preport',
            name: 'SaasPreportRates',
            component: () => import('@/views/saas/rate-management/preport/index.vue'),
            meta: {
              title: '港前运价',
              requiresAuth: false,
              hidden: true // 不显示在菜单中
            }
          },
          // 尾程运价列表页面
          {
            path: 'rate-management/lastmile',
            name: 'SaasLastmileRates',
            component: () => import('@/views/saas/rate-management/lastmile/index.vue'),
            meta: {
              title: '尾程运价',
              requiresAuth: false,
              hidden: true // 不显示在菜单中
            }
          },
          // LCL拼箱运价列表页面
          {
            path: 'rate-management/lcl',
            name: 'SaasLclRates',
            component: () => import('@/views/saas/rate-management/lcl/index.vue'),
            meta: {
              title: 'LCL拼箱运价',
              requiresAuth: false,
              hidden: true
            }
          },
          // 空运运价列表页面
          {
            path: 'rate-management/air',
            name: 'SaasAirRates',
            component: () => import('@/views/saas/rate-management/air/index.vue'),
            meta: {
              title: '空运运价',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: 'rate-management/express',
            name: 'SaasExpressRates',
            component: () => import('@/views/saas/rate-management/express/index.vue'),
            meta: {
              title: '快递运价',
              requiresAuth: false,
              hidden: true
            }
          },
          // 铁路运价列表页面
          {
            path: 'rate-management/rail',
            name: 'SaasRailRates',
            component: () => import('@/views/saas/rate-management/rail/index.vue'),
            meta: {
              title: '铁路运价',
              requiresAuth: false,
              hidden: true
            }
          },
          // 整箱运价详情页面 - 统一路由
          {
            path: 'rate-management/fcl/detail/:mode/:id?',
            name: 'SaasFclRateDetail',
            component: () => import('@/views/saas/rate-management/fcl/detail.vue'),
            meta: {
              title: '运价管理',
              requiresAuth: false,
              hidden: true // 不显示在菜单中
            }
          },
          // 港前运价详情页面 - 统一路由
          {
            path: 'rate-management/preport/detail/:mode/:id?',
            name: 'SaasPreportRateDetail',
            component: () => import('@/views/saas/rate-management/preport/detail.vue'),
            meta: {
              title: '港前运价管理',
              requiresAuth: false,
              hidden: true // 不显示在菜单中
            }
          },
          // 尾程运价详情页面 - 统一路由
          {
            path: 'rate-management/lastmile/detail/:mode/:id?',
            name: 'SaasLastmileRateDetail',
            component: () => import('@/views/saas/rate-management/lastmile/detail.vue'),
            meta: {
              title: '尾程运价管理',
              requiresAuth: false,
              hidden: true // 不显示在菜单中
            }
          },
          // LCL拼箱运价详情页面 - 统一路由
          {
            path: 'rate-management/lcl/detail/:mode/:id?',
            name: 'SaasLclRateDetail',
            component: () => import('@/views/saas/rate-management/lcl/detail.vue'),
            meta: {
              title: '拼箱运价管理',
              requiresAuth: false,
              hidden: true
            }
          },
          // 空运运价详情页面 - 统一路由
          {
            path: 'rate-management/air/detail/:mode/:id?',
            name: 'SaasAirRateDetail',
            component: () => import('@/views/saas/rate-management/air/detail.vue'),
            meta: {
              title: '空运运价管理',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: 'rate-management/express/detail/:mode/:id?',
            name: 'SaasExpressRateDetail',
            component: () => import('@/views/saas/rate-management/express/detail.vue'),
            meta: {
              title: '快递运价管理',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: 'rate-management/rail/detail/:mode/:id?',
            name: 'SaasRailRateDetail',
            component: () => import('@/views/saas/rate-management/rail/detail.vue'),
            meta: {
              title: '铁路运价管理',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: 'contract-management',
            name: 'SaasContractManagement',
            component: () => import('@/views/saas/contract-management/index.vue'),
            meta: {
              title: '合约管理',
              requiresAuth: false,
              order: 3,
              permission: 'module:contract-management:contract-management'
            }
          },
          // 合约管理 - 新增
          {
            path: 'contract-management/add',
            name: 'SaasContractAdd',
            component: () => import('@/views/saas/contract-management/add.vue'),
            meta: {
              title: '新增合约',
              requiresAuth: false,
              hidden: true, // 不显示在菜单中
              activeMenu: 'SaasContractManagement' // 高亮父菜单
            }
          },
          // 合约管理 - 编辑
          {
            path: 'contract-management/edit/:id',
            name: 'SaasContractEdit',
            component: () => import('@/views/saas/contract-management/edit.vue'),
            meta: {
              title: '编辑合约',
              requiresAuth: false,
              hidden: true, // 不显示在菜单中
              activeMenu: 'SaasContractManagement' // 高亮父菜单
            }
          },

          // 附加费维护
          {
            path: 'surcharge-management',
            name: 'SaasSurcharge',
            component: () => import('@/views/saas/surcharge-management/index.vue'),
            meta: {
              title: '附加费维护',
              requiresAuth: false,
              order: 4,
              permission: 'module:surcharge-maintenance:surcharge-maintenance'
            }
          },
          // 附加费维护 - 铁路附加费列表
          {
            path: 'surcharge-management/rail',
            name: 'SaasSurchargeRail',
            component: () => import('@/views/saas/surcharge-management/rail/index.vue'),
            meta: {
              title: '铁路附加费',
              requiresAuth: false,
              hidden: true
            }
          },
          // 附加费维护 - 整箱附加费列表
          {
            path: 'surcharge-management/fcl',
            name: 'SaasSurchargeFcl',
            component: () => import('@/views/saas/rate-management/fcl/index.vue'),
            meta: {
              title: 'FCL整箱运价',
              requiresAuth: false,
              hidden: true // 不显示在菜单中
            }
          },
          // 附加费维护 - 整箱海运附加费详情
          {
            path: 'surcharge-management/fcl/ocean-freight/detail/:mode/:id?',
            name: 'SaasSurchargeFclOceanFreightDetail',
            component: () => import('@views/saas/surcharge-management/fcl/ocean-freight/detail.vue'),
            meta: {
              title: '整箱海运附加费详情',
              requiresAuth: false,
              hidden: true
            }
          },
          // 附加费维护 - 整箱起运港附加费详情
          {
            path: 'surcharge-management/fcl/pol/detail/:mode/:id?',
            name: 'SaasSurchargeFclPolDetail',
            component: () => import('@views/saas/surcharge-management/fcl/pol/detail.vue'),
            meta: {
              title: '整箱起运港附加费详情',
              requiresAuth: false,
              hidden: true
            }
          },
          // 附加费维护 - 整箱卸货港附加费详情
          {
            path: 'surcharge-management/fcl/pod/detail/:mode/:id?',
            name: 'SaasSurchargeFclPodDetail',
            component: () => import('@views/saas/surcharge-management/fcl/pod/detail.vue'),
            meta: {
              title: '整箱卸货港附加费详情',
              requiresAuth: false,
              hidden: true
            }
          },
          // 附加费维护 - 拼箱海运附加费详情
          {
            path: 'surcharge-management/lcl/ocean-freight/detail/:mode/:id?',
            name: 'SaasSurchargeLclOceanFreightDetail',
            component: () => import('@views/saas/surcharge-management/lcl/ocean-freight/detail.vue'),
            meta: {
              title: '拼箱海运附加费详情',
              requiresAuth: false,
              hidden: true
            }
          },
          // 附加费维护 - 拼箱起运港附加费详情
          {
            path: 'surcharge-management/lcl/pol/detail/:mode/:id?',
            name: 'SaasSurchargeLclPolDetail',
            component: () => import('@views/saas/surcharge-management/lcl/pol/detail.vue'),
            meta: {
              title: '拼箱起运港附加费详情',
              requiresAuth: false,
              hidden: true
            }
          },
          // 附加费维护 - 拼箱卸货港附加费详情
          {
            path: 'surcharge-management/lcl/pod/detail/:mode/:id?',
            name: 'SaasSurchargeLclPodDetail',
            component: () => import('@views/saas/surcharge-management/lcl/pod/detail.vue'),
            meta: {
              title: '拼箱卸货港附加费详情',
              requiresAuth: false,
              hidden: true
            }
          },
          // 附加费维护 - 空运附加费详情
          {
            path: 'surcharge-management/air/detail/:mode/:id?',
            name: 'SaasSurchargeAirDetail',
            component: () => import('@views/saas/surcharge-management/air/detail.vue'),
            meta: {
              title: '空运附加费详情',
              requiresAuth: false,
              hidden: true
            }
          },
          // 附加费维护 - 港前附加费详情
          {
            path: 'surcharge-management/pre-port/detail/:mode/:id?',
            name: 'SaasSurchargePrePortDetail',
            component: () => import('@views/saas/surcharge-management/prePort/detail.vue'),
            meta: {
              title: '港前附加费详情',
              requiresAuth: false,
              hidden: true
            }
          },
          // 附加费维护 - 尾程附加费详情
          {
            path: 'surcharge-management/last-mile/detail/:mode/:id?',
            name: 'SaasSurchargeLastMileDetail',
            component: () => import('@views/saas/surcharge-management/lastMile/detail.vue'),
            meta: {
              title: '尾程附加费详情',
              requiresAuth: false,
              hidden: true
            }
          },
          // 附加费维护 - 快递附加费详情
          {
            path: 'surcharge-management/express/detail/:mode/:id?',
            name: 'SaasSurchargeExpressDetail',
            component: () => import('@views/saas/surcharge-management/express/detail.vue'),
            meta: {
              title: '快递附加费详情',
              requiresAuth: false,
              hidden: true
            }
          },
          // 附加费维护 - 铁路附加费详情
          {
            path: 'surcharge-management/rail/detail/:mode/:id?',
            name: 'SaasSurchargeRailDetail',
            component: () => import('@views/saas/surcharge-management/rail/detail.vue'),
            meta: {
              title: '铁路附加费详情',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: 'increase-rules',
            name: 'SaasIncreaseRules',
            component: () => import('@/views/saas/increase-rules/index.vue'),
            meta: {
              title: '加价规则维护',
              requiresAuth: false,
              order: 5,
              permission: 'module:markup-rule-maintenance:markup-rule-maintenance'
            }
          },
          // 整箱加价规则列表页面
          {
            path: 'increase-rules/fcl',
            name: 'SaasFclIncreaseRules',
            component: () => import('@/views/saas/increase-rules/fcl/index.vue'),
            meta: {
              title: '整箱加价规则',
              requiresAuth: false,
              hidden: true // 不显示在菜单中
            }
          },
          // 港前加价规则列表页面
          {
            path: 'increase-rules/preport',
            name: 'SaasPreportIncreaseRules',
            component: () => import('@/views/saas/increase-rules/preport/index.vue'),
            meta: {
              title: '港前加价规则',
              requiresAuth: false,
              hidden: true // 不显示在菜单中
            }
          },
          // 尾程加价规则列表页面
          {
            path: 'increase-rules/lastmile',
            name: 'SaasLastmileIncreaseRules',
            component: () => import('@/views/saas/increase-rules/lastmile/index.vue'),
            meta: {
              title: '尾程加价规则',
              requiresAuth: false,
              hidden: true // 不显示在菜单中
            }
          },
          // 整箱加价规则详情页面 - 统一路由
          {
            path: 'increase-rules/fcl/detail/:mode/:id?',
            name: 'SaasFclIncreaseRulesDetail',
            component: () => import('@/views/saas/increase-rules/fcl/detail.vue'),
            meta: {
              title: '加价规则管理',
              requiresAuth: false,
              hidden: true // 不显示在菜单中
            }
          },
          // 港前加价规则详情页面 - 统一路由
          {
            path: 'increase-rules/preport/detail/:mode/:id?',
            name: 'SaasPreportIncreaseRulesDetail',
            component: () => import('@/views/saas/increase-rules/preport/detail.vue'),
            meta: {
              title: '港前加价规则管理',
              requiresAuth: false,
              hidden: true // 不显示在菜单中
            }
          },
          // 尾程加价规则详情页面 - 统一路由
          {
            path: 'increase-rules/lastmile/detail/:mode/:id?',
            name: 'SaasLastmileIncreaseRulesDetail',
            component: () => import('@/views/saas/increase-rules/lastmile/detail.vue'),
            meta: {
              title: '尾程加价规则管理',
              requiresAuth: false,
              hidden: true // 不显示在菜单中
            }
          },
          // 商务加价规则菜单
          {
            path: 'business-increase-rules',
            name: 'SaasBusinessIncreaseRules',
            component: () => import('@/views/saas/business-increase-rules/index.vue'),
            meta: {
              title: '商务加价规则',
              requiresAuth: false,
              order: 6,
              permission: 'module:business-markup-rule-maintenance:business-markup-rule-maintenance'
            }
          },
          // 商务加价规则 - 海运整箱列表页面
          {
            path: 'business-increase-rules/fcl',
            name: 'SaasBusinessFclIncreaseRules',
            component: () => import('@/views/saas/business-increase-rules/fcl/index.vue'),
            meta: {
              title: '海运整箱',
              requiresAuth: false,
              hidden: true // 不显示在菜单中
            }
          },
          // 商务海运整箱加价规则详情页面
          {
            path: 'business-increase-rules/fcl/detail/:mode/:id?',
            name: 'SaasBusinessFclIncreaseRulesDetail',
            component: () => import('@/views/saas/business-increase-rules/fcl/detail.vue'),
            meta: {
              title: '商务海运整箱加价规则详情',
              requiresAuth: false,
              hidden: true // 不显示在菜单中
            }
          },
          // 商务海运多业务类型共享详情页面
          {
            path: 'business-increase-rules/business-type/detail/:mode/:id?',
            name: 'SaasBusinessSeaIncreaseRulesDetail',
            component: () => import('@/views/saas/business-increase-rules/business-type-detail.vue'),
            meta: {
              title: 'increaseRules.businessSea.pageTitles.view',
              requiresAuth: false,
              hidden: true // 不显示在菜单中
            }
          },
          // 商务港前、尾程、空运、铁路和快递共享详情页面
          {
            path: 'business-increase-rules/single/:businessType/detail/:mode/:id?',
            name: 'SaasBusinessSingleIncreaseRulesDetail',
            component: () => import('@/views/saas/business-increase-rules/single-type-detail.vue'),
            meta: {
              title: 'increaseRules.businessOther.routeTitle',
              requiresAuth: false,
              hidden: true // 不显示在菜单中
            }
          },
          // 按PM原型将分享入口归入运价广场；实际源类型权限由后端逐操作校验。
          {
            path: '',
            name: 'SaasRatePlaza',
            meta: {
              title: 'rateShare.plazaTitle',
              requiresAuth: true,
              order: 8,
              isMenuGroup: true,
              permission: 'module:rate-plaza:rate-plaza',
              preserveRouteMeta: true
            },
            children: [
              {
                path: 'rate-share',
                name: 'SaasRateShare',
                component: () => import('@/views/saas/rate-share/index.vue'),
                meta: {
                  title: 'rateShare.title',
                  permission: 'module:rate-share:rate-share',
                  requiresAuth: true,
                  order: 1,
                  preserveRouteMeta: true
                }
              },
              {
                path: 'rate-subscribe',
                name: 'SaasRateSubscribe',
                component: () => import('@/views/saas/rate-subscribe/index.vue'),
                meta: {
                  title: 'rateSubscribe.title', requiresAuth: true, order: 2,
                  preserveRouteMeta: true, permission: 'module:rate-subscribe:rate-subscribe'
                }
              }
            ]
          },
          // 销售加价规则菜单
          {
            path: 'sales-increase-rules',
            name: 'SaasSalesIncreaseRules',
            component: () => import('@/views/saas/sales-increase-rules/index.vue'),
            meta: {
              title: '销售加价规则维护',
              requiresAuth: false,
              order: 6,
              permission: 'module:sales-markup-rule-maintenance:sales-markup-rule-maintenance'
            }
          },
          // 销售加价规则详情页面
          {
            path: 'sales-increase-rules/detail/:mode/:id?',
            name: 'SaasSalesIncreaseRulesDetail',
            component: () => import('@/views/saas/sales-increase-rules/detail.vue'),
            meta: {
              title: '销售加价规则详情',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: 'sales-quotation',
            name: 'SaasSalesQuotation',
            component: () => import('@views/saas/sales-quotation/index.vue'),
            meta: {
              title: '报价记录',
              requiresAuth: false,
              order: 7,
              permission: 'module:sales-quotation:sales-quotation'
            }
          },
          {
            path: 'rate-query',
            name: 'SaasRateQuery',
            component: () => import('@/views/saas/rate-query/index.vue'),
            meta: {
              title: '运价查询',
              requiresAuth: false,
              order: 2,
              permission: 'module:rate-query:rate-query'
            }
          },
          // 客户端的询价管理
          {
            path: '/saas/inquiry-management',
            name: 'SaasInquiryManagement',
            component: () => import('@/views/saas/inquiry-management/index.vue'),
            meta: {
              title: '询价管理',
              requiresAuth: false,
              order: 2,
              permission: 'module:client-inquiry-management:client-inquiry-management',
              isClient: true
            }
          },
          // 客户端的报价管理
          {
            path: '/saas/quotation-management',
            name: 'SaasClientQuotationManagement',
            component: () => import('@/views/saas/quotation-management/index.vue'),
            meta: {
              title: '报价管理',
              requiresAuth: false,
              order: 3,
              permission: 'module:client-quotation-management:client-quotation-management',
              isClient: true
            }
          },
          // FCL整箱运价查询页面
          {
            path: 'rate-query/fcl',
            name: 'SaasRateQueryFcl',
            component: () => import('@/views/saas/rate-query/fcl/index.vue'),
            meta: {
              title: 'FCL整箱查询',
              requiresAuth: false,
              hidden: true // 不显示在菜单中
            }
          },
          // 港前运价查询页面
          {
            path: 'rate-query/preport',
            name: 'SaasRateQueryPreport',
            component: () => import('@/views/saas/rate-query/preport/index.vue'),
            meta: {
              title: '港前查询',
              requiresAuth: false,
              hidden: true // 不显示在菜单中
            }
          },
          // 尾程运价查询页面
          {
            path: 'rate-query/lastmile',
            name: 'SaasRateQueryLastmile',
            component: () => import('@/views/saas/rate-query/lastmile/index.vue'),
            meta: {
              title: '尾程查询',
              requiresAuth: false,
              hidden: true // 不显示在菜单中
            }
          },
          // LCL拼箱运价查询页面
          {
            path: 'rate-query/lcl',
            name: 'SaasRateQueryLcl',
            component: () => import('@/views/saas/rate-query/lcl/index.vue'),
            meta: {
              title: 'LCL拼箱查询',
              requiresAuth: false,
              hidden: true
            }
          },
          // 空运运价查询页面
          {
            path: 'rate-query/air',
            name: 'SaasRateQueryAir',
            component: () => import('@/views/saas/rate-query/air/index.vue'),
            meta: {
              title: '空运查询',
              requiresAuth: false,
              hidden: true
            }
          },
          // 铁路运价查询页面
          {
            path: 'rate-query/rail',
            name: 'SaasRateQueryRail',
            component: () => import('@/views/saas/rate-query/rail/index.vue'),
            meta: {
              title: '铁路运价查询',
              requiresAuth: false,
              hidden: true
            }
          },
          // 电商运价监控列表页面
          {
            path: 'rate-query/ecommerce/monitor',
            name: 'SaasEcommerceRateMonitor',
            component: () => import('@/views/saas/rate-query/ecommerce/monitor/index.vue'),
            meta: {
              title: '运价监控',
              requiresAuth: false,
              hidden: true
            }
          },
          // 运价查询详情页面路由
          // FCL整箱运价查询详情页面
          {
            path: 'rate-query/fcl/detail/:mode/:id?',
            name: 'SaasRateQueryFclDetail',
            component: () => import('@/views/saas/rate-query/fcl/detail.vue'),
            meta: {
              title: 'FCL整箱运价详情',
              requiresAuth: false,
              hidden: true // 不显示在菜单中
            }
          },
          // 港前运价查询详情页面
          {
            path: 'rate-query/preport/detail/:mode/:priceId/:regionId',
            name: 'SaasRateQueryPreportDetail',
            component: () => import('@/views/saas/rate-query/preport/detail.vue'),
            meta: {
              title: '港前运价详情',
              requiresAuth: false,
              hidden: true // 不显示在菜单中
            }
          },
          // 尾程运价查询详情页面
          {
            path: 'rate-query/lastmile/detail/:mode/:priceId/:regionId/:portId',
            name: 'SaasRateQueryLastmileDetail',
            component: () => import('@/views/saas/rate-query/lastmile/detail.vue'),
            meta: {
              title: '尾程运价详情',
              requiresAuth: false,
              hidden: true // 不显示在菜单中
            }
          },
          // 铁路运价查询详情页面
          {
            path: 'rate-query/rail/detail/:mode/:id?',
            name: 'SaasRateQueryRailDetail',
            component: () => import('@/views/saas/rate-query/rail/detail.vue'),
            meta: {
              title: '铁路运价详情',
              requiresAuth: false,
              hidden: true
            }
          },
          // LCL拼箱运价查询详情页面
          {
            path: 'rate-query/lcl/detail/:mode/:id?',
            name: 'SaasRateQueryLclDetail',
            component: () => import('@/views/saas/rate-query/lcl/detail.vue'),
            meta: {
              title: 'LCL拼箱运价详情',
              requiresAuth: false,
              hidden: true
            }
          },
          // 空运运价查询详情页面
          {
            path: 'rate-query/air/detail/:mode/:id?',
            name: 'SaasRateQueryAirDetail',
            component: () => import('@/views/saas/rate-query/air/detail.vue'),
            meta: {
              title: '空运运价详情',
              requiresAuth: false,
              hidden: true
            }
          },
          // 组合方案查询页面
          {
            path: 'rate-query/combination',
            name: 'SaasRateQueryCombination',
            component: () => import('@/views/saas/rate-query/combination/index.vue'),
            meta: {
              title: '组合方案查询',
              requiresAuth: false,
              hidden: true // 不显示在菜单中
            }
          },
          {
            path: 'rate-query/rail/combination',
            name: 'SaasRailRateQueryCombination',
            component: () => import('@/views/saas/rate-query/rail/combination.vue'),
            meta: {
              title: '铁路组合方案查询',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: 'inquiry-quotation',
            name: 'SaasInquiryQuotation',
            meta: {
              title: '询价报价',
              requiresAuth: false,
              order: 6,
              isMenuGroup: true,
              permission: 'module:inquiry-quotation:inquiry-quotation'
            },
            children: [
              {
                path: '/saas/inquiry-management',
                name: 'SaasInquiryManagement',
                component: () => import('@/views/saas/inquiry-management/index.vue'),
                meta: {
                  title: '询价管理',
                  requiresAuth: false,
                  order: 1,
                  permission: 'module:inquiry-management:inquiry-management'
                }
              },
              {
                path: '/saas/quotation-management',
                name: 'SaasQuotationManagement',
                component: () => import('@/views/saas/quotation-management/index.vue'),
                meta: {
                  title: '报价管理',
                  requiresAuth: false,
                  order: 2,
                  permission: 'module:quotation-management:quotation-management'
                }
              }
            ]
          },
          {
            path: '/saas/inquiry-quote',
            name: 'SaasInquiryQuote',
            component: () => import('@/views/saas/inquiry-quote/index.vue'),
            meta: {
              title: '询报价',
              requiresAuth: false,
              preserveRouteMeta: true,
              permission: 'module:inquiry-quote:inquiry-quote',
              insertAfterPermission: 'module:inquiry-quotation:inquiry-quotation'
            }
          },
          {
            path: '/saas/inquiry-quote/create/:type',
            name: 'SaasInquiryQuoteCreate',
            component: () => import('@/views/saas/inquiry-quote/create.vue'),
            meta: {
              title: '新建询价',
              requiresAuth: false,
              hidden: true,
              permission: 'module:inquiry-quote:inquiry-quote',
              activeMenu: 'SaasInquiryQuote'
            }
          },
          {
            path: '/saas/inquiry-quote/fcl/detail/:inquiryId?',
            name: 'SaasInquiryQuoteFclDetail',
            component: () => import('@/views/saas/inquiry-quote/fcl/detail.vue'),
            meta: {
              title: '海运整箱询报价详情',
              requiresAuth: false,
              hidden: true,
              permission: 'module:inquiry-quote:inquiry-quote',
              activeMenu: 'SaasInquiryQuote'
            }
          },
          {
            path: '/saas/inquiry-quote/lcl/detail/:inquiryId?',
            name: 'SaasInquiryQuoteLclDetail',
            component: () => import('@/views/saas/inquiry-quote/lcl/detail.vue'),
            meta: {
              title: '海运拼箱询报价详情',
              requiresAuth: false,
              hidden: true,
              permission: 'module:inquiry-quote:inquiry-quote',
              activeMenu: 'SaasInquiryQuote'
            }
          },
          {
            path: '/saas/inquiry-quote/sea/detail/:inquiryId?',
            name: 'SaasInquiryQuoteSeaDetail',
            component: () => import('@/views/saas/inquiry-quote/detail-redirect.vue'),
            meta: {
              title: '海运询报价详情',
              requiresAuth: false,
              hidden: true,
              permission: 'module:inquiry-quote:inquiry-quote',
              activeMenu: 'SaasInquiryQuote'
            }
          },
          {
            path: '/saas/inquiry-quote/air/detail/:inquiryId?',
            name: 'SaasInquiryQuoteAirDetail',
            component: () => import('@/views/saas/inquiry-quote/air/detail.vue'),
            meta: {
              title: '空运询报价详情',
              requiresAuth: false,
              hidden: true,
              permission: 'module:inquiry-quote:inquiry-quote',
              activeMenu: 'SaasInquiryQuote'
            }
          },
          {
            // inquiryId 走 query（与后端邮件链接一致）；保留可选 path 兼容旧书签
            path: '/saas/inquiry-quote/detail/:inquiryId?',
            name: 'SaasInquiryQuoteDetail',
            component: () => import('@/views/saas/inquiry-quote/detail-redirect.vue'),
            meta: {
              title: '询报价详情',
              requiresAuth: false,
              hidden: true,
              permission: 'module:inquiry-quote:inquiry-quote',
              activeMenu: 'SaasInquiryQuote'
            }
          },
          {
            path: '/saas/inquiries/create-fcl/:id?',
            name: 'SaasCreateInquiryFcl',
            component: () => import('@/views/saas/inquiry-management/detail.vue'),
            meta: {
              title: '新建整箱询价',
              requiresAuth: false,
              hidden: true // 不显示在菜单中，通过询价管理页面跳转
            }
          },
          {
            path: '/saas/inquiries/unified-create/:type',
            name: 'SaasUnifiedInquiryCreate',
            component: () => import('@/views/saas/inquiry-management/UnifiedInquiryCreatePage.vue'),
            meta: {
              title: '新版询价表单',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: '/saas/inquiries/edit-fcl/:id',
            name: 'SaasEditInquiryFcl',
            component: () => import('@/views/saas/inquiry-management/detail.vue'),
            props: true,
            meta: {
              title: '编辑整箱询价',
              requiresAuth: false,
              hidden: true // 不显示在菜单中，通过询价管理页面跳转
            }
          },
          {
            path: '/saas/inquiries/view-fcl/:id',
            name: 'SaasViewInquiryFcl',
            component: () => import('@/views/saas/inquiry-management/detail.vue'),
            props: true,
            meta: {
              title: '查看整箱询价',
              requiresAuth: false,
              hidden: true // 不显示在菜单中，通过询价管理页面跳转
            }
          },
          {
            path: '/saas/inquiries/create-pre-port/:id?',
            name: 'SaasCreateInquiryPrePort',
            component: () => import('@/views/saas/inquiry-management/detail.vue'),
            meta: {
              title: '新建港前询价',
              requiresAuth: false,
              hidden: true // 不显示在菜单中，通过询价管理页面跳转
            }
          },
          {
            path: '/saas/inquiries/edit-pre-port/:id',
            name: 'SaasEditInquiryPrePort',
            component: () => import('@/views/saas/inquiry-management/detail.vue'),
            props: true,
            meta: {
              title: '编辑港前询价',
              requiresAuth: false,
              hidden: true // 不显示在菜单中，通过询价管理页面跳转
            }
          },
          {
            path: '/saas/inquiries/view-pre-port/:id',
            name: 'SaasViewInquiryPrePort',
            component: () => import('@/views/saas/inquiry-management/detail.vue'),
            props: true,
            meta: {
              title: '查看港前询价',
              requiresAuth: false,
              hidden: true // 不显示在菜单中，通过询价管理页面跳转
            }
          },
          {
            path: '/saas/inquiries/create-last-mile/:id?',
            name: 'SaasCreateInquiryLastMile',
            component: () => import('@/views/saas/inquiry-management/detail.vue'),
            meta: {
              title: '新建尾程询价',
              requiresAuth: false,
              hidden: true // 不显示在菜单中，通过询价管理页面跳转
            }
          },
          {
            path: '/saas/inquiries/edit-last-mile/:id',
            name: 'SaasEditInquiryLastMile',
            component: () => import('@/views/saas/inquiry-management/detail.vue'),
            props: true,
            meta: {
              title: '编辑尾程询价',
              requiresAuth: false,
              hidden: true // 不显示在菜单中，通过询价管理页面跳转
            }
          },
          {
            path: '/saas/inquiries/view-last-mile/:id',
            name: 'SaasViewInquiryLastMile',
            component: () => import('@/views/saas/inquiry-management/detail.vue'),
            props: true,
            meta: {
              title: '查看尾程询价',
              requiresAuth: false,
              hidden: true // 不显示在菜单中，通过询价管理页面跳转
            }
          },
          {
            path: '/saas/inquiries/create-express/:id?',
            name: 'SaasCreateInquiryExpress',
            component: () => import('@/views/saas/inquiry-management/express/detail.vue'),
            meta: {
              title: '新建快递询价',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: '/saas/inquiries/edit-express/:id',
            name: 'SaasEditInquiryExpress',
            component: () => import('@/views/saas/inquiry-management/express/detail.vue'),
            props: true,
            meta: {
              title: '编辑快递询价',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: '/saas/inquiries/view-express/:id',
            name: 'SaasViewInquiryExpress',
            component: () => import('@/views/saas/inquiry-management/express/view.vue'),
            props: true,
            meta: {
              title: '查看快递询价',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: '/saas/inquiries/create-air/:id?',
            name: 'SaasCreateInquiryAir',
            component: () => import('@/views/saas/inquiry-management/air/detail.vue'),
            meta: {
              title: '新建空运询价',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: '/saas/inquiries/edit-air/:id',
            name: 'SaasEditInquiryAir',
            component: () => import('@/views/saas/inquiry-management/air/detail.vue'),
            props: true,
            meta: {
              title: '编辑空运询价',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: '/saas/inquiries/view-air/:id',
            name: 'SaasViewInquiryAir',
            component: () => import('@/views/saas/inquiry-management/air/view.vue'),
            props: true,
            meta: {
              title: '查看空运询价',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: '/saas/inquiries/create-rail/:id?',
            name: 'SaasCreateInquiryRail',
            component: () => import('@/views/saas/inquiry-management/rail/detail.vue'),
            meta: {
              title: '新建铁路询价',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: '/saas/inquiries/edit-rail/:id',
            name: 'SaasEditInquiryRail',
            component: () => import('@/views/saas/inquiry-management/rail/detail.vue'),
            props: true,
            meta: {
              title: '编辑铁路询价',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: '/saas/inquiries/view-rail/:id',
            name: 'SaasViewInquiryRail',
            component: () => import('@/views/saas/inquiry-management/rail/view.vue'),
            props: true,
            meta: {
              title: '查看铁路询价',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: '/saas/quotations/create-fcl',
            name: 'SaasCreateQuotationFcl',
            component: () => import('@/views/saas/quotation-management/detail.vue'),
            meta: {
              title: '新建整箱报价',
              requiresAuth: false,
              hidden: true // 不显示在菜单中，通过询价管理页面跳转
            }
          },
          {
            path: 'quotations/edit-fcl/:id',
            name: 'SaasEditQuotationFcl',
            component: () => import('@/views/saas/quotation-management/detail.vue'),
            props: true,
            meta: {
              title: '编辑整箱报价',
              requiresAuth: false,
              hidden: true // 不显示在菜单中，通过询价管理页面跳转
            }
          },
          {
            path: 'quotations/view-fcl/:id',
            name: 'SaasViewQuotationFcl',
            component: () => import('@/views/saas/quotation-management/detail.vue'),
            props: true,
            meta: {
              title: '查看整箱报价',
              requiresAuth: false,
              hidden: true // 不显示在菜单中，通过询价管理页面跳转
            }
          },
          {
            path: 'quotations/create-pre-port',
            name: 'SaasCreateQuotationPrePort',
            component: () => import('@/views/saas/quotation-management/detail.vue'),
            meta: {
              title: '新建港前报价',
              requiresAuth: false,
              hidden: true // 不显示在菜单中，通过询价管理页面跳转
            }
          },
          {
            path: 'quotations/edit-pre-port/:id',
            name: 'SaasEditQuotationPrePort',
            component: () => import('@/views/saas/quotation-management/detail.vue'),
            props: true,
            meta: {
              title: '编辑港前报价',
              requiresAuth: false,
              hidden: true // 不显示在菜单中，通过询价管理页面跳转
            }
          },
          {
            path: 'quotations/view-pre-port/:id',
            name: 'SaasViewQuotationPrePort',
            component: () => import('@/views/saas/quotation-management/detail.vue'),
            props: true,
            meta: {
              title: '查看港前报价',
              requiresAuth: false,
              hidden: true // 不显示在菜单中，通过询价管理页面跳转
            }
          },
          {
            path: 'quotations/create-last-mile',
            name: 'SaasCreateQuotationLastMile',
            component: () => import('@/views/saas/quotation-management/detail.vue'),
            meta: {
              title: '新建尾程报价',
              requiresAuth: false,
              hidden: true // 不显示在菜单中，通过询价管理页面跳转
            }
          },
          {
            path: 'quotations/edit-last-mile/:id',
            name: 'SaasEditQuotationLastMile',
            component: () => import('@/views/saas/quotation-management/detail.vue'),
            props: true,
            meta: {
              title: '编辑尾程报价',
              requiresAuth: false,
              hidden: true // 不显示在菜单中，通过询价管理页面跳转
            }
          },
          {
            path: 'quotations/view-last-mile/:id',
            name: 'SaasViewQuotationLastMile',
            component: () => import('@/views/saas/quotation-management/detail.vue'),
            props: true,
            meta: {
              title: '查看尾程报价',
              requiresAuth: false,
              hidden: true // 不显示在菜单中，通过询价管理页面跳转
            }
          },
          // CCTOWER-2296 船司账号维护：已迁至「船司电商运价」，旧路径重定向
          {
            path: 'carrier-accounts',
            redirect: '/ecommerce-carrier-rates/ecommerce-rates/platform-account'
          }
        ]
      },

      {
        path: '/saas',
        name: 'Management Freight',
        meta: {
          title: '运价管理',
          icon: 'icon-compass',
          requiresAuth: false,
          order: 4,
          permission: 'module:management-freight:management-freight'
        },
        children: [
          {
            path: 'rate-warehouse',
            name: 'Rate Warehouse',
            component: () => import('@/views/saas/rate-warehouse/index.vue'),
            meta: {
              title: '运价库',
              requiresAuth: false,
              order: 1
            }
          },
          {
            path: 'freight-surcharge-management',
            name: 'ManagementFreightSurcharge',
            component: () => import('@/views/saas/freight-surcharge-management/index.vue'),
            meta: {
              title: '附加费管理',
              requiresAuth: false,
              order: 2,
              permission: 'module:management-freight-surcharge:management-freight-surcharge'
            }
          },
          {
            path: 'freight-surcharge-management/express/detail/:mode/:id?',
            name: 'ManagementFreightSurchargeExpressDetail',
            component: () => import('@/views/saas/surcharge-management/express/detail.vue'),
            props: {
              entryPath: '/saas/freight-surcharge-management'
            },
            meta: {
              title: '快递附加费详情',
              requiresAuth: false,
              hidden: true,
              permission: 'module:management-freight-surcharge:management-freight-surcharge',
              preserveRouteMeta: true,
              activeMenu: 'ManagementFreightSurcharge'
            }
          },
          {
            path: 'shokz-rate-query',
            name: 'ShokzRateQuery',
            component: () => import('@/views/saas/shokz-rate-query/index.vue'),
            meta: {
              title: '运价查询',
              requiresAuth: false,
              order: 2,
              permission: 'module:shokz-rate-query:shokz-rate-query'
            }
          },
          {
            path: 'shokz-rate-query/combination',
            name: 'ShokzRateQueryCombination',
            component: () => import('@/views/saas/rate-query/combination/index.vue'),
            meta: {
              title: '韶音组合方案查询',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'ShokzRateQuery'
            }
          },
          {
            path: 'rate-warehouse/fcl/detail/:mode/:id?',
            name: 'SaasRateWarehouseFclDetail',
            component: () => import('@/views/saas/rate-warehouse/fcl/detail.vue'),
            meta: {
              title: '运价库海运整箱',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: 'rate-warehouse/lcl/detail/:mode/:id?',
            name: 'SaasRateWarehouseLclDetail',
            component: () => import('@/views/saas/rate-warehouse/lcl/detail.vue'),
            meta: {
              title: '运价库海运拼箱',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: 'rate-warehouse/air/detail/:mode/:id?',
            name: 'SaasRateWarehouseAirDetail',
            component: () => import('@/views/saas/rate-warehouse/air/detail.vue'),
            meta: {
              title: '运价库空运',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: 'rate-warehouse/lastmile/detail/:mode/:id?',
            name: 'SaasRateWarehouseLastmileDetail',
            component: () => import('@/views/saas/rate-warehouse/lastmile/detail.vue'),
            meta: {
              title: '运价库尾程',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: 'rate-warehouse/preport/detail/:mode/:id?',
            name: 'SaasRateWarehousePreportDetail',
            component: () => import('@/views/saas/rate-warehouse/preport/detail.vue'),
            meta: {
              title: '运价库港前',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: 'rate-warehouse/express/detail/:mode/:id?',
            name: 'SaasRateWarehouseExpressDetail',
            component: () => import('@/views/saas/rate-management/express/detail.vue'),
            meta: {
              title: '运价库快递运价',
              requiresAuth: false,
              hidden: true
            }
          },
           {
            path: '/saas/shokz-inquiry-management',
            name: 'SHOKZInquiryManagement',
            component: () => import('@/views/saas/shokz-inquiry-management/index.vue'),
            meta: {
              title: '询价管理',
              requiresAuth: false,
              order: 3,
              permission: 'module:shokz-inquiry-management:shokz-inquiry-management'
            }
          },
          {
            path: '/saas/shokz-inquiry-management/lcl',
            name: 'ShokzInquiryLcl',
            component: () => import('@/views/saas/shokz-inquiry-management/lcl/index.vue'),
            meta: {
              title: '海运拼箱询价',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SHOKZInquiryManagement'
            }
          },
          {
            path: '/saas/shokz-inquiry-management/lcl/detail',
            name: 'ShokzInquiryLclDetail',
            component: () => import('@/views/saas/shokz-inquiry-management/lcl/detail.vue'),
            meta: {
              title: '海运拼箱询价详情',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SHOKZInquiryManagement'
            }
          },
          {
            path: '/saas/shokz-inquiries/create-lcl/:id?',
            name: 'ShokzCreateInquiryLcl',
            component: () => import('@/views/saas/shokz-inquiry-management/lcl/detail.vue'),
            meta: {
              title: '新建拼箱询价',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SHOKZInquiryManagement'
            }
          },
          {
            path: '/saas/shokz-inquiries/edit-lcl/:id',
            name: 'ShokzEditInquiryLcl',
            component: () => import('@/views/saas/shokz-inquiry-management/lcl/detail.vue'),
            props: true,
            meta: {
              title: '编辑拼箱询价',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SHOKZInquiryManagement'
            }
          },
          {
            path: '/saas/shokz-inquiries/view-lcl/:id',
            name: 'ShokzViewInquiryLcl',
            component: () => import('@/views/saas/shokz-inquiry-management/lcl/detail.vue'),
            props: true,
            meta: {
              title: '查看拼箱询价',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SHOKZInquiryManagement'
            }
          },
          {
            path: '/saas/shokz-inquiries/create-express/:id?',
            name: 'ShokzCreateInquiryExpress',
            component: () => import('@/views/saas/inquiry-management/express/detail.vue'),
            meta: {
              title: '新建快递询价',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SHOKZInquiryManagement'
            }
          },
          {
            path: '/saas/shokz-inquiries/edit-express/:id',
            name: 'ShokzEditInquiryExpress',
            component: () => import('@/views/saas/inquiry-management/express/detail.vue'),
            props: true,
            meta: {
              title: '编辑快递询价',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SHOKZInquiryManagement'
            }
          },
          {
            path: '/saas/shokz-inquiries/view-express/:id',
            name: 'ShokzViewInquiryExpress',
            component: () => import('@/views/saas/inquiry-management/express/inquiry-detail.vue'),
            props: true,
            meta: {
              title: '查看快递询价',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SHOKZInquiryManagement'
            }
          },
          {
            path: '/saas/shokz-inquiries/create-fcl/:id?',
            name: 'ShokzCreateInquiryFcl',
            component: () => import('@/views/saas/shokz-inquiry-management/detail.vue'),
            meta: {
              title: '新建整箱询价',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SHOKZInquiryManagement'
            }
          },
          {
            path: '/saas/shokz-inquiries/edit-fcl/:id',
            name: 'ShokzEditInquiryFcl',
            component: () => import('@/views/saas/shokz-inquiry-management/detail.vue'),
            props: true,
            meta: {
              title: '编辑整箱询价',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SHOKZInquiryManagement'
            }
          },
          {
            path: '/saas/shokz-inquiries/create-pre-port/:id?',
            name: 'ShokzCreateInquiryPrePort',
            component: () => import('@/views/saas/shokz-inquiry-management/detail.vue'),
            meta: {
              title: '新建港前询价',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SHOKZInquiryManagement'
            }
          },
          {
            path: '/saas/shokz-inquiries/edit-pre-port/:id',
            name: 'ShokzEditInquiryPrePort',
            component: () => import('@/views/saas/shokz-inquiry-management/detail.vue'),
            props: true,
            meta: {
              title: '编辑港前询价',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SHOKZInquiryManagement'
            }
          },
          {
            path: '/saas/shokz-inquiries/view-pre-port/:id',
            name: 'ShokzViewInquiryPrePort',
            component: () => import('@/views/saas/shokz-inquiry-management/detail.vue'),
            props: true,
            meta: {
              title: '查看港前询价',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SHOKZInquiryManagement'
            }
          },
          {
            path: '/saas/shokz-inquiries/create-last-mile/:id?',
            name: 'ShokzCreateInquiryLastMile',
            component: () => import('@/views/saas/shokz-inquiry-management/detail.vue'),
            meta: {
              title: '新建尾程询价',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SHOKZInquiryManagement'
            }
          },
          {
            path: '/saas/shokz-inquiries/edit-last-mile/:id',
            name: 'ShokzEditInquiryLastMile',
            component: () => import('@/views/saas/shokz-inquiry-management/detail.vue'),
            props: true,
            meta: {
              title: '编辑尾程询价',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SHOKZInquiryManagement'
            }
          },
          {
            path: '/saas/shokz-inquiries/view-last-mile/:id',
            name: 'ShokzViewInquiryLastMile',
            component: () => import('@/views/saas/shokz-inquiry-management/detail.vue'),
            props: true,
            meta: {
              title: '查看尾程询价',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SHOKZInquiryManagement'
            }
          },
          // ========== 新版询价详情页（统一入口） ==========
          {
            path: '/saas/shokz-inquiry-detail/:type/:id',
            name: 'ShokzInquiryDetailNew',
            component: () => import('@/views/saas/shokz-inquiry-management/inquiry-detail/index.vue'),
            props: true,
            meta: {
              title: '询价详情',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: '/saas/shokz-inquiry-management/air',
            name: 'ShokzInquiryAir',
            component: () => import('@/views/saas/shokz-inquiry-management/air/index.vue'),
            meta: {
              title: '空运询价',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SHOKZInquiryManagement'
            }
          },
          {
            path: '/saas/shokz-inquiries/create-air/:id?',
            name: 'ShokzCreateInquiryAir',
            component: () => import('@/views/saas/shokz-inquiry-management/air/detail.vue'),
            meta: {
              title: '新建空运询价',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SHOKZInquiryManagement'
            }
          },
          {
            path: '/saas/shokz-inquiries/edit-air/:id',
            name: 'ShokzEditInquiryAir',
            component: () => import('@/views/saas/shokz-inquiry-management/air/detail.vue'),
            meta: {
              title: '编辑空运询价',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SHOKZInquiryManagement'
            }
          },
          {
            path: '/saas/shokz-quotation-management',
            name: 'SHOKZQuotationManagement',
            component: () => import('@/views/saas/shokz-quotation-management/index.vue'),
            meta: {
              title: '报价管理',
              requiresAuth: false,
              order: 3,
              permission: 'module:shokz-quotation-management:shokz-quotation-management'
            }
          },
          {
            path: '/saas/shokz-quotations/create-fcl',
            name: 'ShokzCreateQuotationFcl',
            component: () => import('@/views/saas/shokz-quotation-management/detail.vue'),
            meta: {
              title: '新建整箱报价',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SHOKZQuotationManagement'
            }
          },
          {
            path: '/saas/shokz-quotations/edit-fcl/:id',
            name: 'ShokzEditQuotationFcl',
            component: () => import('@/views/saas/shokz-quotation-management/detail.vue'),
            props: true,
            meta: {
              title: '编辑整箱报价',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SHOKZQuotationManagement'
            }
          },
          {
            path: '/saas/shokz-quotations/view-fcl/:id',
            name: 'ShokzViewQuotationFcl',
            component: () => import('@/views/saas/shokz-quotation-management/detail.vue'),
            props: true,
            meta: {
              title: '查看整箱报价',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SHOKZQuotationManagement'
            }
          },
          {
            path: '/saas/shokz-quotations/create-pre-port',
            name: 'ShokzCreateQuotationPrePort',
            component: () => import('@/views/saas/shokz-quotation-management/detail.vue'),
            meta: {
              title: '新建港前报价',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SHOKZQuotationManagement'
            }
          },
          {
            path: '/saas/shokz-quotations/edit-pre-port/:id',
            name: 'ShokzEditQuotationPrePort',
            component: () => import('@/views/saas/shokz-quotation-management/detail.vue'),
            props: true,
            meta: {
              title: '编辑港前报价',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SHOKZQuotationManagement'
            }
          },
          {
            path: '/saas/shokz-quotations/view-pre-port/:id',
            name: 'ShokzViewQuotationPrePort',
            component: () => import('@/views/saas/shokz-quotation-management/detail.vue'),
            props: true,
            meta: {
              title: '查看港前报价',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SHOKZQuotationManagement'
            }
          },
          {
            path: '/saas/shokz-quotations/create-last-mile',
            name: 'ShokzCreateQuotationLastMile',
            component: () => import('@/views/saas/shokz-quotation-management/detail.vue'),
            meta: {
              title: '新建尾程报价',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SHOKZQuotationManagement'
            }
          },
          {
            path: '/saas/shokz-quotations/edit-last-mile/:id',
            name: 'ShokzEditQuotationLastMile',
            component: () => import('@/views/saas/shokz-quotation-management/detail.vue'),
            props: true,
            meta: {
              title: '编辑尾程报价',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SHOKZQuotationManagement'
            }
          },
          {
            path: '/saas/shokz-quotations/view-last-mile/:id',
            name: 'ShokzViewQuotationLastMile',
            component: () => import('@/views/saas/shokz-quotation-management/detail.vue'),
            props: true,
            meta: {
              title: '查看尾程报价',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SHOKZQuotationManagement'
            }
          },
          {
            path: '/saas/shokz-quotations/quote-compare/:inquiryId',
            name: 'ShokzQuoteCompare',
            component: () => import('@/views/saas/shokz-quotation-management/quote-compare.vue'),
            props: true,
            meta: {
              title: '报价汇总',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: 'bidding',
            name: 'BiddingManagementGroup',
            meta: {
              title: '招投标管理',
              requiresAuth: false,
              order: 4,
              permission: 'module:bidding-management:bidding-management'
            },
            children: [
              {
                path: 'tender-management',
                name: 'TenderManagement',
                component: () => import('@/views/saas/bidding-management/tender-management/index.vue'),
                meta: {
                  title: '招标管理',
                  requiresAuth: false,
                  order: 1,
                  permission: 'module:tender-management:tender-management'
                }
              },
              {
                path: 'tender-management/create',
                name: 'TenderManagementCreate',
                component: () => import('@/views/saas/bidding-management/tender-management/detail.vue'),
                props: { mode: 'create' },
                meta: {
                  title: '新增招标',
                  requiresAuth: false,
                  hidden: true,
                  activeMenu: 'TenderManagement',
                  permission: 'module:tender-management:tender-management'
                }
              },
              {
                path: 'tender-management/edit/:id',
                name: 'TenderManagementEdit',
                component: () => import('@/views/saas/bidding-management/tender-management/detail.vue'),
                props: route => ({ mode: 'edit', id: String(route.params.id || '') }),
                meta: {
                  title: '编辑招标',
                  requiresAuth: false,
                  hidden: true,
                  activeMenu: 'TenderManagement',
                  permission: 'module:tender-management:tender-management'
                }
              },
              {
                path: 'tender-management/view/:id',
                name: 'TenderManagementView',
                component: () => import('@/views/saas/bidding-management/tender-management/detail.vue'),
                props: route => ({ mode: 'view', id: String(route.params.id || '') }),
                meta: {
                  title: '招标详情',
                  requiresAuth: false,
                  hidden: true,
                  activeMenu: 'TenderManagement',
                  permission: 'module:tender-management:tender-management'
                }
              },
              {
                path: 'tender-management/bid-book/:bidId',
                name: 'BiddingBidBookDetail',
                component: () => import('@/views/bidding/supplier-bid/index.vue'),
                meta: {
                  title: '应标标书',
                  requiresAuth: true,
                  hidden: true,
                  permission: 'module:tender-management:tender-management'
                }
              },
              {
                path: 'tender-management/evaluation/:tenderId',
                name: 'BiddingCrossEvaluation',
                component: () => import('@/views/saas/bidding-management/tender-management/evaluation.vue'),
                meta: {
                  title: '跨标书评标',
                  requiresAuth: true,
                  hidden: true,
                  activeMenu: 'TenderManagement',
                  permission: 'module:tender-management:tender-management'
                }
              },
              {
                path: 'tender-management/winner-assignment/:tenderId',
                name: 'BiddingWinnerAssignment',
                component: () => import('@/views/saas/bidding-management/tender-management/winner-assignment.vue'),
                meta: {
                  title: '设定中标人',
                  requiresAuth: true,
                  hidden: true,
                  activeMenu: 'TenderManagement',
                  permission: 'module:tender-management:tender-management'
                }
              }
            ]
          }
        ]
      },

      // [CCTOWER-2270] 船司电商运价
      {
        path: '/ecommerce-carrier-rates',
        name: 'EcommerceCarrierRates',
        meta: {
          title: '船司电商运价',
          icon: 'icon-file',
          requiresAuth: false,
          permission: 'module:ecommerce-carrier-rates:ecommerce-carrier-rates',
          order: 4.1,
          isMenuGroup: true
        },
        children: [
          {
            path: 'ecommerce-rates',
            name: 'NavEcommerceRates',
            component: () => import('@/views/ecommerce-carrier-rates/standalone.vue'),
            meta: { title: '电商运价', requiresAuth: false, order: 1 }
          },
          {
            path: 'ecommerce-rates/price-watch',
            name: 'NavEcommercePriceWatch',
            component: () => import('@/views/saas/rate-query/ecommerce/monitor/index.vue'),
            meta: { title: '运价监控', requiresAuth: false, order: 2 }
          },
          // CCTOWER-2296 船司账号维护（从超级运价迁入）
          {
            path: 'ecommerce-rates/platform-account',
            name: 'CarrierAccountList',
            component: () => import('@/views/system-config/carrier-account/index.vue'),
            meta: { title: '船司账号维护', requiresAuth: false, order: 2.5 }
          },
          {
            path: 'ecommerce-rates/daily',
            name: 'NavEcommerceDailyRates',
            component: () => import('@/views/ecommerce-carrier-rates/daily/index.vue'),
            meta: { title: '今日运价', requiresAuth: false, order: 3 }
          },
          {
            path: 'ecommerce-rates/subscription',
            name: 'NavEcommerceSubscription',
            component: () => import('@/views/ecommerce-carrier-rates/subscription/index.vue'),
            meta: { title: '每日推送配置', requiresAuth: false, order: 4 }
          }
        ]
      },

      // 订单中心
      {
        path: '/orders',
        name: 'Orders',
        meta: {
          title: '订单中心',
          icon: 'icon-list',
          requiresAuth: false,
          order: 4,
          isMenuGroup: true,
          permission: 'module:order-center:order-center'
        },
        children: [
          {
            path: 'management',
            name: 'OrderManagement',
            component: () => import('@/views/order/index.vue'),
            meta: {
              title: '订单管理',
              requiresAuth: false,
              order: 1,
              permission: 'module:order-management:order-management'
            }
          },
          {
            path: 'tracking-query',
            name: 'OrderTrackingQuery',
            component: () => import('@/views/order/tracking-query/index.vue'),
            meta: {
              title: '运踪查询',
              requiresAuth: false,
              // 兜底顺序：锚定权限缺失（未配该菜单）时生效
              order: 3,
              // 独立权限码，租户端 platform_menu / 客户端 client_privilege 各需配一条同名菜单
              permission: 'module:tracking-query:tracking-query',
              // 固定排在「车架号查询」之后（车架号查询的 order 由后端 sort 覆盖，写死值对不齐）
              insertAfterPermission: 'module:chassis-query:chassis-query'
            }
          },
          {
            path: 'chassis-query',
            name: 'ChassisQuery',
            component: () => import('@/views/order/chassis-query/index.vue'),
            meta: {
              title: '车架号查询',
              requiresAuth: false,
              order: 2,
              permission: 'module:chassis-query:chassis-query'
            }
          },
          {
            path: 'transport-plan',
            name: 'TransportPlan',
            component: () => import('@/views/order/transport-plan/index.vue'),
            meta: {
              title: '运输计划单',
              requiresAuth: false,
              order: 4,
              permission: 'module:transport-plan:transport-plan'
            }
          },
          {
            path: 'transport-consignment',
            name: 'TransportConsignment',
            component: () => import('@/views/order/transport-consignment/index.vue'),
            meta: {
              title: '运输委托单',
              requiresAuth: false,
              order: 5,
              permission: TRANSPORT_CONSIGNMENT_PERM.menu
            }
          },
          {
            path: 'waybill',
            name: 'Waybill',
            component: () => import('@/views/order/waybill/index.vue'),
            meta: {
              title: '运单',
              requiresAuth: false,
              order: 6,
              permission: 'module:waybill:waybill'
            }
          },
          {
            path: 'waybill/:waybillId',
            name: 'WaybillDetail',
            component: () => import('@/views/order/waybill/detail.vue'),
            meta: {
              title: '运单详情',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'Waybill'
            },
            props: true
          },
          {
            path: 'transport-consignment/:consignmentId',
            name: 'TransportConsignmentDetail',
            component: () => import('@/views/order/transport-consignment/detail.vue'),
            meta: {
              title: '运输委托单详情',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'TransportConsignment'
            },
            props: true
          },
          {
            path: 'transport-plan/:planId',
            name: 'TransportPlanDetail',
            component: () => import('@/views/order/transport-plan/detail.vue'),
            meta: {
              title: '运输计划详情',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'TransportPlan'
            },
            props: true
          },
          {
            path: 'transport-plan/:planId/edit',
            name: 'TransportPlanEdit',
            component: () => import('@/views/order/transport-plan/detail.vue'),
            meta: {
              title: '编辑运输计划单',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'TransportPlan'
            },
            props: true
          },
          {
            path: 'distribution-shipment',
            name: 'DistributionShipment',
            component: () => import('@/views/order/distribution-shipment/index.vue'),
            meta: {
              title: '发货申请单',
              requiresAuth: false,
              order: 7,
              permission: 'module:distribution-shipment:distribution-shipment'
            }
          },
          {
            path: 'distribution-shipment/create',
            name: 'DistributionShipmentCreate',
            component: () => import('@/views/order/distribution-shipment/create.vue'),
            meta: {
              title: '新增发货申请单',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'DistributionShipment',
              permission: 'module:distribution-shipment:distribution-shipment'
            }
          },
          {
            path: 'distribution-shipment/detail/:id',
            name: 'DistributionShipmentDetail',
            component: () => import('@/views/order/distribution-shipment/detail.vue'),
            meta: {
              title: '发货申请单详情',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'DistributionShipment',
              permission: 'module:distribution-shipment:distribution-shipment'
            },
            props: true
          },
          {
            path: 'distribution-shipment/edit/:id',
            name: 'DistributionShipmentEdit',
            component: () => import('@/views/order/distribution-shipment/create.vue'),
            meta: {
              title: '编辑发货申请单',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'DistributionShipment',
              permission: 'module:distribution-shipment:distribution-shipment'
            },
            props: true
          },
          {
            path: 'distribution-shipment/:shipmentNo',
            name: 'DistributionShipmentByNo',
            component: () => import('@/views/order/distribution-shipment/index.vue'),
            meta: {
              title: '发货申请单',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'DistributionShipment',
              permission: 'module:distribution-shipment:distribution-shipment'
            },
            props: true
          },
          {
            path: 'detail/:orderId',
            name: 'OrderDetail',
            component: () => import('@/views/order/detail.vue'),
            meta: {
              title: '订单详情',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'OrderManagement'
            },
            props: true
          },
          // 运单列表/详情路由由运单模块同事按原型注册，名称 Waybill / WaybillDetail，路径见 SHOKZ-71 文档
          // 订舱页面 - 支持 :orderId 或 create
          {
            path: 'tasks/booking/:orderId',
            name: 'OrderBookingTask',
            component: () => import('@/views/order/tasks/booking-refactored.vue'),
            meta: {
              title: '订舱申请',
              requiresAuth: false,
              hidden: true
            },
            props: true
          },
          // 统一的提单补料任务页面
          {
            path: 'tasks/bl-addition/:orderId',
            name: 'OrderBLAddition',
            component: () => import('@/views/order/tasks/bl-addition.vue'),
            meta: {
              title: '提单补料',
              requiresAuth: false,
              hidden: true
            },
            props: true
          },
          // BC上传任务页面
          {
            path: 'tasks/bc-upload/:orderId',
            name: 'OrderBCUpload',
            component: () => import('@/views/order/tasks/bc-upload.vue'),
            meta: {
              title: 'BC上传',
              requiresAuth: false,
              hidden: true
            },
            props: true
          }
        ]
      },

      {
        path: '/report-center',
        name: 'ReportCenter',
        component: () => import('@/views/report-center/transport-consignment/index.vue'),
        beforeEnter: canEnterReportCenter,
        meta: {
          title: 'reportCenter.title',
          icon: 'icon-file',
          requiresAuth: false,
          order: 4.4,
          permission: 'module:report-center:report-center'
        }
      },
      // 兼容原报告地址及已保存的页签，统一进入单层菜单并保留查询参数。
      {
        path: '/report-center/transport-consignment',
        name: 'ReportCenterTransportConsignment',
        redirect: to => ({ name: 'ReportCenter', query: to.query, hash: to.hash, replace: true }),
        meta: {
          title: 'reportCenter.title',
          requiresAuth: false,
          hidden: true,
          permission: 'module:report-center:report-center'
        }
      },

      // 账单详情（仅从运输委托单页签进入，无独立菜单）
      {
        path: '/billing/payable/:billId',
        name: 'BillingPayableDetail',
        component: () => import('@/views/billing/payable/detail.vue'),
        meta: {
          title: '应付账单详情',
          requiresAuth: false,
          hidden: true,
          permission: 'module:settlement-statement:settlement-statement'
        },
        props: true
      },
      {
        path: '/billing/receivable/:billId',
        name: 'BillingReceivableDetail',
        component: () => import('@/views/billing/receivable/detail.vue'),
        meta: {
          title: '应收账单详情',
          requiresAuth: false,
          hidden: true,
          permission: 'module:settlement-statement:settlement-statement'
        },
        props: true
      },

      // 结算管理（排序见 route-permission.applySettlementMenuOrder）
      {
        path: '/settlement',
        name: 'Settlement',
        meta: {
          title: '结算',
          icon: 'icon-calculator',
          requiresAuth: false,
          order: 4.5,
          isMenuGroup: true,
          permission: 'module:settlement:settlement'
        },
        children: [
          {
            path: 'settlement-statement',
            name: 'SettlementStatement',
            component: () => import('@/views/settlement/settlement-statement/index.vue'),
            meta: {
              title: '结算账单',
              requiresAuth: false,
              order: 1,
              permission: 'module:settlement-statement:settlement-statement'
            }
          }
        ]
      },

      // 船期中心
      {
        path: '/shipping',
        name: 'ShippingScheduleCenter',
        meta: {
          title: '船期中心',
          icon: 'icon-compass', // 使用指南针图标，更符合船期导航的概念
          requiresAuth: false,
          order: 5,
          isMenuGroup: true,
          permission: 'module:schedule-center:schedule-center'
        },
        children: [
          {
            path: 'lineMaintenance',
            name: 'LineMaintenance',
            component: () => import('@/views/shipping/lineMaintenance/index.vue'),
            meta: {
              title: '航线维护',
              requiresAuth: false,
              order: 1,
              permission: 'module:route-maintenance:route-maintenance'
            }
          },
          {
            path: 'lineMaintenance/line/detail/create',
            name: 'LineMaintenanceCreate',
            component: () => import('@/views/shipping/lineMaintenance/line/detail.vue'),
            meta: {
              title: '新增航线',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: 'lineMaintenance/line/detail/edit/:id',
            name: 'LineMaintenanceEdit',
            component: () => import('@/views/shipping/lineMaintenance/line/detail.vue'),
            meta: {
              title: '编辑航线',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: 'lineMaintenance/line/detail/view/:id',
            name: 'LineMaintenanceView',
            component: () => import('@/views/shipping/lineMaintenance/line/detail.vue'),
            meta: {
              title: '航线详情',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: 'schedule-query',
            name: 'ShipSchedule',
            component: () => import('@/views/shipping/shipSchedule/index.vue'),
            meta: {
              title: '船期查询',
              requiresAuth: false,
              order: 2,
              permission: 'module:schedule-query:schedule-query'
            }
          }
        ]
      },

      // 客商中心 - 包含企业管理、供应商管理、用户管理和角色权限管理
      {
        path: '/customer',
        name: 'Customer',
        meta: {
          title: '客商中心',
          icon: 'icon-users',
          requiresAuth: false,
          order: 5,
          isMenuGroup: true,
          permission: 'module:customer-center:customer-center'
        },
        children: [
          {
            path: 'company-management',
            name: 'CustomerCompanyManagement',
            component: () => import('@views/customer/company-management/index.vue'),
            meta: {
              title: '企业管理',
              requiresAuth: false,
              order: 1,
              permission: 'module:company-management:company-management'
            }
          },
          {
            path: 'company-management/add',
            name: 'CustomerCompanyAdd',
            component: () => import('@views/customer/company-management/company-form.vue'),
            meta: {
              title: '添加企业',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: 'company-management/edit/:id',
            name: 'CustomerCompanyEdit',
            component: () => import('@views/customer/company-management/company-form.vue'),
            meta: {
              title: '编辑企业',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: 'supplier-management',
            name: 'SupplierManagement',
            component: () => import('@views/customer/supplier-management/index.vue'),
            meta: {
              title: '供应商管理',
              requiresAuth: false,
              order: 2,
              permission: 'module:supplier-management:supplier-management'
            }
          },
          {
            path: 'supplier-management/detail/:id',
            name: 'SupplierManagementDetail',
            component: () => import('@views/customer/supplier-management/detail.vue'),
            meta: {
              title: '供应商打分详情',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: 'user-management',
            name: 'CustomerUserManagement',
            component: () => import('@views/customer/user-management/index.vue'),
            meta: {
              title: '用户管理',
              requiresAuth: false,
              order: 3,
              permission: 'module:user-management:user-management'
            }
          },
          {
            path: 'role-auth-management',
            name: 'RoleAuthManagement',
            component: () => import('@views/customer/role-auth-management/index.vue'),
            meta: {
              title: '角色权限管理',
              requiresAuth: false,
              order: 4,
              permission: 'module:role-auth-management:role-auth-management'
            }
          },
          {
            path: 'role-auth-management/role-permission-config/:roleId',
            name: 'RolePermissionConfig',
            component: () => import('@views/customer/role-auth-management/role-permission-config.vue'),
            meta: {
              title: '权限设置',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: 'role-auth-management/role-customer-config/:roleId',
            name: 'RoleCustomerConfig',
            component: () => import('@views/customer/role-auth-management/role-customer-config.vue'),
            meta: {
              title: '配置客商',
              requiresAuth: false,
              hidden: true
            }
          }
        ]
      },

      // 用户中心
      {
        path: '/user-center',
        name: 'UserCenter',
        meta: {
          title: '用户中心',
          icon: 'icon-profile',
          requiresAuth: false,
          order: 6,
          isMenuGroup: true,
          permission: 'module:user-center:user-center'
        },
        children: [
          {
            path: 'profile',
            name: 'UserProfile',
            component: () => import('@views/user-center/user/index.vue'),
            meta: {
              title: '个人信息',
              requiresAuth: false,
              order: 1,
              permission: 'module:personal-info:personal-info'
            }
          },
          {
            path: 'company-profile',
            name: 'UserCompanyProfile',
            component: () => import('@views/user-center/company/profile/index.vue'),
            meta: {
              title: '企业信息',
              requiresAuth: false,
              order: 2,
              permission: 'module:company-info:company-info'
            }
          },
          {
            path: 'quote-settings',
            name: 'UserQuoteSettings',
            component: () => import('@views/user-center/quote-settings/index.vue'),
            meta: {
              title: '报价设置',
              requiresAuth: false,
              order: 3,
              permission: 'module:quote-settings:quote-settings',
              preserveRouteMeta: true
            }
          },
          {
            path: 'company-certification',
            name: 'CompanyCertification',
            component: () => import('@views/user-center/company/certification/index.vue'),
            meta: {
              title: '企业认证',
              requiresAuth: false,
              hidden: true
            }
          }
        ]
      },
      // 应用中心
      {
        path: '/application-center',
        name: 'ApplicationCenter',
        component: () => import('@/views/application-center/index.vue'),
        meta: {
          title: '应用中心',
          icon: 'icon-apps',
          requiresAuth: false,
          order: 8,
          permission: 'module:app-center:app-center'
        }
      },

      // 工具化模块 - 已移除，运踪查询现在只能通过独立链接访问
      // {
      //   path: '/tools',
      //   name: 'Tools',
      //   meta: {
      //     title: '工具化模块',
      //     icon: 'icon-settings',
      //     requiresAuth: false,
      //     order: 999,
      //     isMenuGroup: true
      //     // 如果需要权限控制，可以在后端配置权限后添加：permission: 'module:tools:tools'
      //   },
      //   children: [
      //     {
      //       path: 'tracking-query',
      //       name: 'TrackingQuery',
      //       component: () => import('@/views/tools/tracking-query/index.vue'),
      //       meta: {
      //         title: '运踪查询',
      //         requiresAuth: false,
      //         order: 1
      //         // 如果需要权限控制，可以在后端配置权限后添加：permission: 'module:tools:tracking-query'
      //       }
      //     }
      //   ]
      // },

      // API中心
      {
        path: '/api-center',
        name: 'ApiCenter',
        meta: {
          title: 'API中心',
          icon: 'icon-api',
          requiresAuth: false,
          order: 9,
          isMenuGroup: true,
          permission: 'module:api-center:api-center'
        },
        children: [
          {
            path: 'list',
            name: 'ApiList',
            component: () => import('@/views/api-center/list/index.vue'),
            meta: {
              title: 'API列表',
              requiresAuth: false,
              order: 1,
              permission: 'module:api-list:api-list'
            }
          },
          {
            path: 'docs/detail',
            name: 'ApiDocDetail',
            component: () => import('@views/api-center/docs/index.vue'),
            meta: {
              title: 'API详情',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: 'test',
            name: 'ApiTest',
            component: () => import('@/views/api-center/test/index.vue'),
            meta: {
              title: '接口测试',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: 'settings',
            name: 'ApiSettings',
            component: () => import('@/views/api-center/settings/index.vue'),
            meta: {
              title: 'API设置',
              requiresAuth: false,
              order: 2,
              permission: 'module:api-settings:api-settings'
            }
          },
          {
            path: 'sync-target-settings',
            name: 'SyncTargetSettings',
            component: () => import('@/views/api-sync/target-setting/index.vue'),
            meta: {
              title: '对接方设置',
              requiresAuth: false,
              order: 3,
              permission: 'module:sync-target-settings:sync-target-settings'
            }
          },
          {
            path: 'api-sync-settings',
            name: 'ApiSyncSettings',
            component: () => import('@/views/api-sync/sync-setting/index.vue'),
            meta: {
              title: '同步设置',
              requiresAuth: false,
              order: 4,
              permission: 'module:api-sync-settings:api-sync-settings'
            }
          }
        ]
      },

      // 系统设置 - 包含员工管理等功能
      {
        path: '/system-settings',
        name: 'SystemSettings',
        meta: {
          title: '系统设置',
          icon: 'icon-settings',
          requiresAuth: false,
          order: 10,
          isMenuGroup: true,
          permission: 'module:system-settings:system-settings'
        },
        children: [
          {
            path: 'config-management',
            name: 'ConfigManagement',
            component: () => import('@/views/system-management/config-management/index.vue'),
            meta: {
              title: '配置管理',
              requiresAuth: false,
              order: 4,
              permission: 'module:config-management:config-management'
            }
          },
          {
            path: 'permission-management',
            name: 'PermissionManagement',
            component: () => import('@/views/system-management/permission-management/index.vue'),
            meta: {
              title: '权限管理',
              requiresAuth: false,
              order: 5,
              permission: 'module:permission-management:permission-management'
            }
          },
          {
            path: 'position-management',
            name: 'SystemPositionManagement',
            component: () => import('@/views/system-management/position-management/index.vue'),
            meta: {
              title: 'menu.positionManagement',
              requiresAuth: false,
              requiresShokz: true,
              order: 7,
              permission: 'module:position-management:position-management'
            }
          },
          {
            path: 'employee-management',
            name: 'SystemEmployeeManagement',
            component: () => import('@/views/system-management/staff-management/staff-management.vue'),
            meta: {
              title: 'menu.staffManagement',
              requiresAuth: false,
              order: 6,
              permission: 'module:staff-management:staff-management'
            }
          },
          {
            path: 'employee-management/add',
            name: 'SystemEmployeeAdd',
            component: () => import('@/views/system-management/staff-management/staff-form.vue'),
            meta: {
              title: '添加员工',
              requiresAuth: false,
              hidden: true,
              permission: 'module:staff-management:staff-management'
            }
          },
          {
            path: 'employee-management/edit/:id',
            name: 'SystemEmployeeEdit',
            component: () => import('@/views/system-management/staff-management/staff-form.vue'),
            meta: {
              title: '编辑员工',
              requiresAuth: false,
              hidden: true,
              permission: 'module:staff-management:staff-management'
            }
          },
          {
            path: 'personalization-config',
            name: 'PersonalizationConfigGroup',
            redirect: { name: 'PersonalizationConfig' },
            meta: {
              title: '个性化配置',
              requiresAuth: false,
              order: 7,
              permission: 'module:personalization-config:personalization-config',
              isMenuGroup: true
            },
            children: [
              {
                path: 'index',
                name: 'PersonalizationConfig',
                component: () => import('@/views/system-management/personalization-config.vue'),
                meta: {
                  title: '个性化配置',
                  requiresAuth: false,
                  order: 1,
                  permission: 'module:personalization-config:personalization-config',
                  // 禁止被后端同权限码 sort 覆盖，保证排在公司新闻之上
                  preserveRouteMeta: true
                }
              },
              {
                path: 'company-news',
                name: 'CompanyNews',
                component: () => import('@/views/system-management/company-news/index.vue'),
                meta: {
                  title: '公司新闻',
                  requiresAuth: false,
                  order: 2,
                  // 与个性化配置共用权限，避免后端尚未配置 company-news 权限码时被滤掉
                  permission: 'module:personalization-config:personalization-config',
                  // 禁止被后端同权限码的菜单名覆盖为「个性化配置」
                  preserveRouteMeta: true,
                  activeMenu: 'PersonalizationConfigGroup'
                }
              }
            ]
          },
          {
            path: 'template-config',
            name: 'TemplateConfig',
            component: () => import('@/views/system-management/template-config/index.vue'),
            meta: {
              title: '模板设置',
              requiresAuth: false,
              order: 8,
              permission: 'module:template-config:template-config'
            }
          },
          {
            path: 'supplier-quotation-config',
            name: 'SupplierQuotationConfig',
            component: () => import('@/views/system-management/supplier-quotation-config/list.vue'),
            meta: {
              title: '供应商报价配置',
              permission: 'module:supplier-quotation-config:supplier-quotation-config',
              requiresAuth: false,
              order: 9
            }
          },
          {
            path: 'supplier-quotation-config/create',
            name: 'SupplierQuotationConfigCreate',
            component: () => import('@/views/system-management/supplier-quotation-config/create.vue'),
            meta: {
              title: '新建模板',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SupplierQuotationConfig'
            }
          },
          {
            path: 'supplier-quotation-config/edit/:id',
            name: 'SupplierQuotationConfigEdit',
            component: () => import('@/views/system-management/supplier-quotation-config/create.vue'),
            meta: {
              title: '编辑模板',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SupplierQuotationConfig'
            }
          },
          {
            path: 'supplier-quotation-config/detail/:id',
            name: 'SupplierQuotationConfigDetail',
            component: () => import('@/views/system-management/supplier-quotation-config/detail.vue'),
            meta: {
              title: '模板详情',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SupplierQuotationConfig'
            }
          },
          {
            path: 'supplier-quotation-config/quotation-list',
            name: 'SupplierQuotationList',
            component: () => import('@/views/system-management/supplier-quotation-config/quotation-list.vue'),
            meta: {
              title: '供应商报价列表',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SupplierQuotationConfig'
            }
          },
          {
            path: 'supplier-quotation-config/quotation-detail/:id',
            name: 'SupplierQuotationDetail',
            component: () => import('@/views/system-management/supplier-quotation-config/quotation-detail.vue'),
            meta: {
              title: '供应商报价详情',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'SupplierQuotationConfig'
            }
          },
          {
            path: 'task-management',
            name: 'TaskManagement',
            component: () => import('@/views/system-management/task-management/index.vue'),
            meta: {
              title: '任务管理',
              requiresAuth: false,
              order: 9,
              permission: 'module:task-management:task-management'
            }
          },
          {
            path: 'task-management/import-task-detail/:id',
            name: 'ImportTaskDetail',
            component: () => import('@/views/system-management/task-management/import-task-detail.vue'),
            meta: {
              title: '导入任务详情',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: 'task-management/pre-port-import-task-detail/:id',
            name: 'PrePortImportTaskDetail',
            component: () => import('@/views/system-management/task-management/pre-port-import-task-detail.vue'),
            meta: {
              title: 'taskManagement.detail.prePortImportTaskDetailTitle',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: 'task-management/ratio-progress-detail/:id',
            name: 'RatioProgressDetail',
            component: () => import('@/views/system-management/task-management/ratio-progress-detail.vue'),
            meta: {
              title: 'taskManagement.detail.ratioProgress.title',
              requiresAuth: false,
              hidden: true
            }
          },
          {
            path: 'approval-process',
            name: 'ApprovalProcess',
            component: () => import('@/views/system-management/approval-process/index.vue'),
            meta: {
              title: '流程管理',
              requiresAuth: false,
              order: 10,
              permission: 'module:approval-process:approval-process'
            }
          },
          {
            path: 'approval-process/create',
            name: 'ApprovalProcessCreate',
            component: () => import('@/views/system-management/approval-process/editor.vue'),
            meta: {
              title: '新建流程',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'ApprovalProcess',
              permission: 'module:approval-process:approval-process'
            }
          },
          {
            path: 'approval-process/edit/:workflowGroupId',
            name: 'ApprovalProcessEdit',
            component: () => import('@/views/system-management/approval-process/editor.vue'),
            meta: {
              title: '编辑流程',
              requiresAuth: false,
              hidden: true,
              activeMenu: 'ApprovalProcess',
              permission: 'module:approval-process:approval-process'
            }
          },
          {
            path: 'node-management',
            name: 'NodeManagement',
            component: () => import('@/views/system-management/node-management/index.vue'),
            meta: {
              title: '节点管理',
              requiresAuth: false,
              order: 10,
              permission: 'module:node-management:node-management'
            }
          },
          {
            path: 'business-manager-config',
            name: 'BusinessManagerConfig',
            component: () => import('@/views/system-management/business-manager-config/index.vue'),
            meta: {
              title: '业务负责人设置',
              requiresAuth: false,
              order: 11,
              permission: 'module:business-manager-config:business-manager-config'
            }
          },
          {
            path: 'alert-center',
            name: 'AlertCenter',
            component: () => import('@/views/system-management/alert-center/index.vue'),
            meta: {
              title: '预警中心',
              requiresAuth: false,
              order: 11,
              permission: 'module:alert-center:alert-center'
            }
          },
          {
            path: 'tracking-config',
            name: 'TrackingConfig',
            component: () => import('@/views/system-management/tracking-config/index.vue'),
            meta: {
              title: '运踪配置管理',
              requiresAuth: false,
              order: 12,
              permission: 'module:tracking-config:tracking-config'
            }
          }
        ]
      },

      // 异常管理中心 - 业务异常处理工作台
      {
        path: '/exception-center',
        name: 'ExceptionCenter',
        redirect: '/exception-center/processing',
        meta: {
          title: '异常管理中心',
          icon: 'icon-exclamation-circle',
          requiresAuth: true,
          order: 10,
          isMenuGroup: true,
          permission: 'module:exception-center:exception-center'
        },
        children: [
          {
            path: 'processing',
            name: 'ExceptionProcessing',
            component: () => import('@/views/system-management/business-exception/index.vue'),
            meta: {
              title: '异常处理',
              requiresAuth: true,
              order: 1,
              permission: 'module:exception-center:processing'
            }
          },
          {
            path: 'basic-data',
            name: 'ExceptionBasicData',
            component: () => import('@/views/system-management/business-exception/basic-data.vue'),
            meta: {
              title: '异常基础数据',
              requiresAuth: true,
              order: 2,
              permission: 'module:exception-center:basic-data'
            }
          },
          {
            path: 'config',
            name: 'BusinessExceptionRuleConfig',
            component: () => import('@/views/system-management/business-exception/config.vue'),
            meta: {
              title: '异常规则配置',
              requiresAuth: true,
              order: 3,
              permission: 'module:exception-center:config'
            }
          }
        ]
      },

      // 基础资料维护
      {
        path: '/basic-data',
        name: 'BasicData',
        meta: {
          title: '基础资料维护',
          icon: 'icon-storage',
          requiresAuth: false,
          order: 11,
          isMenuGroup: true,
          permission: 'module:base-data:base-data'
        },
        children: [
          {
            path: 'ports',
            name: 'PortManagement',
            component: () => import('@/views/basic-data/ports/index.vue'),
            meta: {
              title: '港口管理',
              requiresAuth: false,
              order: 1,
              permission: 'module:port-management:port-management'
            }
          },
          {
            path: 'port-group',
            name: 'PortGroupManagement',
            component: () => import('@/views/basic-data/port-group/index.vue'),
            meta: {
              title: '港口组管理',
              requiresAuth: false,
              order: 2,
              permission: 'module:port-management:port-group-management'
            }
          },
          {
            path: 'carrier-management',
            name: 'CarrierManagement',
            component: () => import('@/views/basic-data/carrier-management/index.vue'),
            meta: {
              title: '承运人管理',
              requiresAuth: false,
              order: 3,
              permission: 'module:carrier-management:carrier-management'
            }
          },
          {
            path: 'country-region-management',
            name: 'CountryRegionManagement',
            component: () => import('@/views/basic-data/country-region-management/index.vue'),
            meta: {
              title: '国家（地区）',
              requiresAuth: false,
              order: 3,
              permission: 'module:country-region:country-region'
            }
          },
          {
            path: 'china-administrative-division',
            name: 'ChinaAdministrativeDivision',
            component: () => import('@/views/basic-data/china-administrative-division/index.vue'),
            meta: {
              title: '行政区划',
              requiresAuth: false,
              order: 4,
              permission: 'module:china-administrative-division:china-administrative-division'
            }
          },
          {
            path: 'overseas-warehouse-management',
            name: 'OverseasWarehouseManagement',
            component: () => import('@/views/basic-data/overseas-warehouse-management/index.vue'),
            meta: {
              title: '仓库管理',
              requiresAuth: false,
              order: 5,
              permission: 'module:overseas-warehouse:overseas-warehouse'
            }
          },
          {
            path: 'zipcode-management',
            name: 'ZipcodeManagement',
            component: () => import('@/views/basic-data/zipcode-management/index.vue'),
            meta: {
              title: '邮编管理',
              requiresAuth: false,
              order: 6,
              permission: 'module:zipcode-management:zipcode-management'
            }
          },
          {
            path: 'route-management',
            name: 'RouteManagement',
            component: () => import('@/views/basic-data/route-v2-management/index.vue'),
            meta: {
              title: '航线管理',
              requiresAuth: false,
              order: 7,
              permission: 'module:route-management:route-management'
            }
          },
          {
            path: 'container-management',
            name: 'ContainerManagement',
            component: () => import('@/views/basic-data/container-management/index.vue'),
            meta: {
              title: '集装箱管理',
              requiresAuth: false,
              order: 8,
              permission: 'module:container-management:container-management'
            }
          },
          {
            path: 'package-unit-management',
            name: 'PackageUnitManagement',
            component: () => import('@/views/basic-data/package-unit-management/index.vue'),
            meta: {
              title: '包装单位',
              requiresAuth: false,
              order: 9,
              permission: 'module:packaging-unit:packaging-unit'
            }
          },
          {
            path: 'cargo-type-management',
            name: 'CargoTypeManagement',
            component: () => import('@/views/basic-data/cargo-type-management/index.vue'),
            meta: {
              title: '货物类型（品名）',
              requiresAuth: false,
              order: 9,
              permission: 'module:cargo-type:cargo-type'
            }
          },
          {
            path: 'transport-terms-management',
            name: 'TransportTermsManagement',
            component: () => import('@/views/basic-data/transport-terms-management/index.vue'),
            meta: {
              title: '运输条款',
              requiresAuth: false,
              order: 10,
              permission: 'module:transport-terms:transport-terms'
            }
          },
          {
            path: 'trade-terms-management',
            name: 'TradeTermsManagement',
            component: () => import('@/views/basic-data/trade-terms-management/index.vue'),
            meta: {
              title: '贸易条款',
              requiresAuth: false,
              order: 11,
              permission: 'module:trade-terms:trade-terms'
            }
          },
          {
            path: 'currency-management',
            name: 'CurrencyManagement',
            component: () => import('@/views/basic-data/currency-management/index.vue'),
            meta: {
              title: '币种管理',
              requiresAuth: false,
              order: 12,
              permission: 'module:currency-management:currency-management'
            }
          },
          {
            path: 'exchange-rate-management',
            name: 'ExchangeRateManagement',
            component: () => import('@/views/basic-data/exchange-rate-management/index.vue'),
            meta: {
              title: '汇率设置',
              requiresAuth: false,
              order: 13,
              permission: 'module:exchange-rate-management:exchange-rate-management'
            }
          },
          {
            path: 'calculation-unit-management',
            name: 'CalculationUnitManagement',
            component: () => import('@/views/basic-data/calculation-unit-management/index.vue'),
            meta: {
              title: '计费单位',
              requiresAuth: false,
              order: 14,
              permission: 'module:billing-unit:billing-unit'
            }
          },
          {
            path: 'charge-management',
            name: 'ChargeManagement',
            component: () => import('@/views/basic-data/charge-management/index.vue'),
            meta: {
              title: '费用管理',
              requiresAuth: false,
              order: 15,
              permission: 'module:cost-management:cost-management'
            }
          },
          {
            path: 'ship-agent-management',
            name: 'ShipAgentManagement',
            component: () => import('@/views/basic-data/ship-agent-management/index.vue'),
            meta: {
              title: '船舶代理',
              requiresAuth: false,
              order: 16,
              permission: 'module:shipping-agent:shipping-agent'
            }
          },
          {
            path: 'ship-data-management',
            name: 'ShipDataManagement',
            component: () => import('@/views/basic-data/ship-data-management/index.vue'),
            meta: {
              title: '船舶资料',
              requiresAuth: false,
              order: 17,
              permission: 'module:vessel-information:vessel-information'
            }
          },
          {
            path: 'terminal-management',
            name: 'TerminalManagement',
            component: () => import('@/views/basic-data/terminal-management/index.vue'),
            meta: {
              title: '码头管理',
              requiresAuth: false,
              order: 18,
              permission: 'module:terminal-management:terminal-management'
            }
          },
          {
            path: 'product-material-data',
            name: 'ProductMaterialData',
            component: () => import('@/views/basic-data/product-material-data/index.vue'),
            meta: {
              title: '产品物料数据',
              requiresAuth: false,
              order: 19,
              permission: 'module:product-material-data:product-material-data'
            }
          },
          {
            path: 'document-control',
            name: 'DocumentControl',
            component: () => import('@/views/basic-data/document-control/index.vue'),
            meta: {
              title: '文控资料',
              requiresAuth: false,
              order: 20,
              permission: 'module:document-control:document-control'
            }
          },
          {
            path: 'supplier-contract-management',
            name: 'SupplierContractManagement',
            component: () => import('@/views/basic-data/supplier-contract-management/index.vue'),
            meta: {
              title: '供应商合同管理',
              requiresAuth: false,
              requiresShokz: true,
              preserveRouteMeta: true,
              order: 21,
              permission: 'module:base-data:base-data'
            }
          },
          {
            path: 'supplier-contract-management/:contractNumber',
            name: 'SupplierContractDetail',
            component: () => import('@/views/basic-data/supplier-contract-management/detail.vue'),
            meta: {
              title: '供应商合同详情',
              requiresAuth: false,
              requiresShokz: true,
              preserveRouteMeta: true,
              hidden: true,
              activeMenu: 'SupplierContractManagement',
              permission: 'module:base-data:base-data'
            }
          }
        ]
      },

      // 销售百箱
      {
        path: '/sales',
        name: 'Sales',
        meta: {
          title: '销售百宝箱',
          icon: 'icon-rocket',
          requiresAuth: false,
          order: 12,
          isMenuGroup: true,
          permission: 'module:sales:sales'
        },
        children: [
          {
            path: 'marketing-leads',
            name: 'MarketingLeads',
            component: () => import('@/views/sales/marketing-leads/index.vue'),
            meta: {
              title: '营销线索',
              requiresAuth: false,
              order: 1,
              permission: 'module:marketing-leads:marketing-leads'
            }
          },
          {
            path: 'ai-customer-acquisition',
            name: 'AiCustomerAcquisition',
            component: () => import('@/views/sales/ai-customer-acquisition/index.vue'),
            meta: {
              title: 'AI获客',
              requiresAuth: false,
              order: 2,
              permission: 'module:ai-customer-acquisition:ai-customer-acquisition'
            }
          }
        ]
      }
    ]
  },

  // SSO 授权页面（支持可选 type 参数：login|bind）
  {
    path: '/sso/auth/:provider/:type?',
    name: 'SsoAuth',
    component: () => import('@/views/auth/sso-auth.vue'),
    meta: {
      title: 'SSO授权',
      hidden: true
    }
  },

  // 404 路由匹配
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/404.vue'),
    meta: {
      title: 'error.404.title',
      hidden: true
    }
  }
]
